import React, { useState } from "react";
import { Card, SectionTitle, PageHeader } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import PeriodPicker, { DeltaPill } from "../components/ui-bj/PeriodPicker";
import { useOverview, updateAirtable } from "../hooks/useAirtable";
import { fmtDate, timeAgo, str } from "../lib/airtableHelpers";
import { RevenueByProductChart } from "../components/overview/RevenueByProductChart";
import { relabel } from "../lib/displayNames";

const Stat = ({ testid, label, value, children }) => (
  <div className="bj-card p-6 flex flex-col gap-2 min-h-[136px] min-w-0" data-testid={testid}>
    <div className="bj-label">{label}</div>
    <div className="bj-metric">{value}</div>
    <div className="text-[12px] text-[#5E4D40]">{children}</div>
  </div>
);

const ServiceLines = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((n, r) => n + r.value, 0);
  return (
    <Card data-testid="service-lines-chart">
      <SectionTitle right={<span className="bj-tnum">{total} active services</span>}>Clients by service line</SectionTitle>
      {rows.length === 0 && <div className="text-[#5E4D40] text-sm py-6">Tag clients with Active Services in Airtable to populate.</div>}
      <ul className="flex flex-col gap-4">
        {rows.map((s, i) => (
          <li key={s.name} data-testid={`service-line-${i}`} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_2rem] items-center gap-4 text-sm">
            <span className="text-[#2A1C15]">{relabel(s.name)}</span>
            <span className="h-2 bg-[rgba(42,28,21,0.18)]">
              <span className="block h-full bg-[#6E5022]" style={{ width: `${(s.value / max) * 100}%` }} />
            </span>
            <span className="bj-tnum text-right">{s.value}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

const OverviewPage = () => {
  const [period, setPeriod] = useState(null); // null = current month
  const { data, loading, error, refetch } = useOverview(period);

  if (loading) return <Loading label="Loading Command Center" />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (!data) return null;

  const { stats, previous_stats, period: p, service_lines, document_queue, recent_activity, upcoming_appointments } = data;

  const doDoc = async (id, status) => {
    await updateAirtable("documents", id, { "Review Status": status });
    refetch();
  };

  return (
    <div data-testid="overview-page">
      <PageHeader
        title="Command Center"
        subtitle={`Real-time view of JC3 Wealth Legacy Group. Viewing ${p?.label || "current period"}${p?.is_current_month ? " (live)" : ""}, compared with ${p?.previous_label || "prior month"}.`}
        right={<PeriodPicker value={p?.current} onChange={setPeriod} />}
      />

      <div className="grid grid-cols-1 min-[960px]:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <Stat testid="stat-total-clients" label="Total clients" value={stats.total_clients.toLocaleString()}>
          <span className="bj-tnum">+{stats.new_clients} new this {p?.is_current_month ? "month" : "period"} (prev: {previous_stats?.new_clients ?? 0})</span>
        </Stat>
        <Stat testid="stat-revenue-collected" label="Revenue collected" value={`$${(stats.revenue_collected || 0).toLocaleString()}`}>
          <DeltaPill current={stats.revenue_collected} previous={previous_stats?.revenue_collected} isCurrency />
        </Stat>
        <Stat testid="stat-revenue-pending" label="Revenue pending" value={`$${(stats.revenue_pending || 0).toLocaleString()}`}>
          <DeltaPill current={stats.revenue_pending} previous={previous_stats?.revenue_pending} isCurrency invertColors />
        </Stat>
        <Stat testid="stat-revenue-overdue" label="Revenue overdue" value={`$${(stats.revenue_overdue || 0).toLocaleString()}`}>
          <DeltaPill current={stats.revenue_overdue} previous={previous_stats?.revenue_overdue} isCurrency invertColors />
        </Stat>
      </div>

      <div className="grid grid-cols-1 min-[960px]:grid-cols-2 gap-5 mb-6">
        <RevenueByProductChart />
        <ServiceLines rows={service_lines} />
      </div>

      <div className="grid grid-cols-1 min-[960px]:grid-cols-2 gap-5 mb-6">
        <Card data-testid="recent-activity-panel">
          <SectionTitle>Recent client activity</SectionTitle>
          <ul className="flex flex-col">
            {recent_activity.length === 0 && <li className="text-[#5E4D40] text-sm py-3">No events logged yet.</li>}
            {recent_activity.map((a, i) => (
              <li key={a.id || i} data-testid={`activity-row-${i}`} className="flex items-start justify-between gap-4 py-3.5 border-b border-[rgba(42,28,21,0.18)] last:border-b-0 text-sm">
                <span className="min-w-0 break-words">
                  {str(a.client, "Anonymous")} <span className="text-[#5E4D40]">{a.action}{a.page ? ` · ${a.page}` : ""}</span>
                </span>
                <span className="text-[#5E4D40] text-xs whitespace-nowrap">{timeAgo(a.timestamp)}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card data-testid="upcoming-appointments-panel">
          <SectionTitle right="Upcoming">Upcoming appointments</SectionTitle>
          <ul className="flex flex-col">
            {upcoming_appointments.length === 0 && <li className="text-[#5E4D40] text-sm py-3">No appointments scheduled.</li>}
            {upcoming_appointments.map((a, i) => (
              <li key={`${a.date}-${a.client}-${i}`} data-testid={`appointment-row-${i}`} className="flex items-start justify-between gap-4 py-3.5 border-b border-[rgba(42,28,21,0.18)] last:border-b-0 text-sm">
                <span className="min-w-0 break-words">
                  {a.client} <span className="text-[#5E4D40]">· {a.consultant || "Unassigned"}</span>
                </span>
                <span className="text-[#5E4D40] text-xs whitespace-nowrap bj-tnum">{fmtDate(a.date)}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card data-testid="document-queue-panel">
        <SectionTitle right={<span className="bj-tnum">{document_queue.length} pending</span>}>Document review queue</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="text-left">
                {["Client", "Document", "Service", "Uploaded", "Status", "Actions"].map((h) => (
                  <th key={h} className={`bj-table-header py-3 pr-4 ${h === "Actions" ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {document_queue.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[#5E4D40] text-sm">No documents awaiting review.</td></tr>
              )}
              {document_queue.map((doc, i) => (
                <tr key={doc.id} data-testid={`doc-queue-row-${i}`} className="bj-table-row text-sm">
                  <td className="py-3 pr-4">{doc.client}</td>
                  <td className="py-3 pr-4">{doc.doc}</td>
                  <td className="py-3 pr-4 text-[#5E4D40]">{relabel(doc.service)}</td>
                  <td className="py-3 pr-4 text-[#5E4D40] bj-tnum whitespace-nowrap">{fmtDate(doc.date)}</td>
                  <td className="py-3 pr-4"><span className="pr-tag text-[#6E5022]">{doc.status}</span></td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`doc-approve-${doc.id}`} onClick={() => doDoc(doc.id, "Approved")} className="pr-btn-outline">Approve</button>
                      <button data-testid={`doc-resubmit-${doc.id}`} onClick={() => doDoc(doc.id, "Resubmit")} className="pr-btn-outline">Resubmit</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default OverviewPage;
